import { ADDRESS_PROPS, USA } from 'platform/user/profile/vap-svc/constants';
import { areAddressesEqual } from 'platform/user/profile/vap-svc/util';
import { ADDRESS_TYPES } from 'platform/forms/address/helpers';

/**
 * Maps an addressUI-shaped form address (street / street2 / street3 / city /
 * state / postalCode / country, from
 * platform/forms-system/src/js/web-component-patterns, using its DEFAULT
 * keys — a form that remaps keys via addressUI's `keys` option must adapt
 * its data before this pattern can compare it) onto the VA Profile shape
 * (addressLine1..3 / city / stateCode / zipCode / countryCodeIso3), so it
 * can be compared with — or displayed next to — the profile's mailing
 * address. addressUI reuses `state` and `postalCode` for international
 * addresses, so those map to `province` / `internationalPostalCode` when
 * the country isn't USA.
 *
 * `addressType` is set so AddressView/formatAddress renders the full
 * address (its formatter switches on addressType; without it only street
 * and city appear). Military APO/FPO/DPO addresses carry country USA and
 * render correctly under the DOMESTIC branch (city, state code, zip).
 *
 * @param {Object} [formAddress] - addressUI-shaped address from form data
 * @returns {Object} VA Profile-shaped address
 */
export const formAddressToProfileShape = (formAddress = {}) => {
  const { street, street2, street3, city, state, postalCode, country } =
    formAddress;
  const isInternational = !!country && country !== USA.COUNTRY_ISO3_CODE;

  return {
    addressLine1: street,
    addressLine2: street2,
    addressLine3: street3,
    city,
    countryCodeIso3: country || USA.COUNTRY_ISO3_CODE,
    addressType: isInternational
      ? ADDRESS_TYPES.international
      : ADDRESS_TYPES.domestic,
    ...(isInternational
      ? { province: state, internationalPostalCode: postalCode }
      : { stateCode: state, zipCode: postalCode }),
  };
};

/**
 * Normalizes a single address component for comparison: trims, collapses
 * internal whitespace and upper-cases so cosmetic differences between what
 * VA Profile returns and what was entered into the form don't read as a
 * change.
 * @param {*} value
 * @returns {string}
 */
const normalizeValue = value =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();

/**
 * The address components that identify a mailing address (ADDRESS_PROPS,
 * the same list vap-svc compares), normalized, with empty values dropped.
 * @param {Object} address - VA Profile-shaped address
 * @returns {Object}
 */
const normalizedForComparison = address => {
  const result = {};
  ADDRESS_PROPS.forEach(key => {
    const value = normalizeValue(address?.[key]);
    if (value) {
      result[key] = value;
    }
  });
  return result;
};

/**
 * Whether two VA Profile-shaped mailing addresses are the same address.
 * Delegates the equality itself to vap-svc's `areAddressesEqual` (the
 * platform's one definition of "same profile address") over normalized
 * copies, so cosmetic case/whitespace differences don't count and
 * non-address metadata (`id` / `updatedAt` / `addressType`) is ignored.
 * @param {Object} a
 * @param {Object} b
 * @returns {boolean}
 */
export const isSameMailingAddress = (a, b) =>
  areAddressesEqual(normalizedForComparison(a), normalizedForComparison(b));
